import React, { memo, useEffect, useRef, useState } from 'react';
import styles from './style.module.css';
import { TranscriptionViewer } from '../TranscriptionViewer/TranscriptionViewer';
import { DialogTranscription } from '../../../../models/CallAttempt/CallAttempt';
import { dateTime } from '../../../../utils/formatDate';
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { AudioPlayer } from '../AudioPlayer/AudioPlayer';

export interface CallAttemptCardProps {
  recordingId: string;
  recordingPath?: string | null;
  callDateTime: string;
  callState: string;
  hasRecording: boolean;
  audioUrl: string | null;
  onLoadAudio: () => void;
  transcription: DialogTranscription[] | null;
  isTranscriptionLoading: boolean;
  onLoadTranscription: () => void;
  hasTranscription: boolean;
  attemptNumber?: number;
  isLast?: boolean;
}

export const CallAttemptCard: React.FC<CallAttemptCardProps> = memo(
  ({
    recordingId,
    recordingPath,
    callDateTime,
    callState,
    hasRecording,
    audioUrl,
    onLoadAudio,
    transcription,
    isTranscriptionLoading,
    onLoadTranscription,
    hasTranscription,
    attemptNumber,
    isLast,
  }) => {
    const [isTranscriptionExpanded, setIsTranscriptionExpanded] =
      useState<boolean>(false);

    const onLoadAudioRef = useRef(onLoadAudio);
    onLoadAudioRef.current = onLoadAudio;

    const onLoadTranscriptionRef = useRef(onLoadTranscription);
    onLoadTranscriptionRef.current = onLoadTranscription;

    useEffect(() => {
      if (hasRecording && audioUrl === null) {
        onLoadAudioRef.current();
      }
    }, [hasRecording, audioUrl]);

    useEffect(() => {
      if (isTranscriptionExpanded && transcription === null) {
        onLoadTranscriptionRef.current();
      }
    }, [isTranscriptionExpanded, transcription]);

    const recordingFileName = recordingPath
      ?.split(/[\\/]/)
      .pop()
      ?.replace(/\.[^/.]+$/, '');
    const audioFileName = `${recordingFileName || recordingId}.mp3`;
    const formattedDateTime = callDateTime ? dateTime(callDateTime) : '';
    const stateBadgeLabel = callState.trim() || 'Неизвестно';

    return (
      <div className={styles.card}>
        <div className={styles.stepper}>
          <div className={styles.stepperNumber}>{attemptNumber}</div>
          {!isLast && <div className={styles.stepperLine} />}
        </div>
        <div className={styles.cardContent}>
          <div className={styles.cardHeader}>
            <span className={styles.dateTime}>{formattedDateTime}</span>
            <span className={styles.stateBadge}>{stateBadgeLabel}</span>
          </div>

          <div className={styles.audioSection}>
            {hasRecording ? (
              audioUrl ? (
                <div className={styles.audioControls}>
                  <AudioPlayer src={audioUrl} />
                  <div className={styles.iconActions}>
                    <Tooltip title="Скачать запись">
                      <a
                        href={audioUrl}
                        download={audioFileName}
                        className={styles.iconLink}
                        aria-label="Скачать запись"
                      >
                        <DownloadOutlined />
                      </a>
                    </Tooltip>
                    {hasTranscription && (
                      <Tooltip title="Расшифровка диалога">
                        <Button
                          type="text"
                          icon={<FileTextOutlined />}
                          className={styles.iconButton}
                          aria-label="Расшифровка диалога"
                          onClick={() =>
                            setIsTranscriptionExpanded((prev) => !prev)
                          }
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              ) : (
                <div className={styles.loadingText}>Загрузка записи...</div>
              )
            ) : (
              <div className={styles.noRecording}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                    stroke="#8C8C8C"
                    strokeWidth="1.5"
                  />
                  <line
                    x1="3.5"
                    y1="3.5"
                    x2="12.5"
                    y2="12.5"
                    stroke="#8C8C8C"
                    strokeWidth="1.5"
                  />
                </svg>
                <span>Запись отсутствует: диалог не начат</span>
              </div>
            )}
          </div>

          {hasTranscription && isTranscriptionExpanded && (
            <>
              <div className={styles.transcriptionContent}>
                {isTranscriptionLoading || transcription === null ? (
                  <div className={styles.loadingText}>
                    Загрузка расшифровки...
                  </div>
                ) : (
                  <TranscriptionViewer transcription={transcription} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  },
);

CallAttemptCard.displayName = 'CallAttemptCard';
